<template>
  <v-card rounded="md" class="ma-4">
    <v-card-title class="d-flex align-center justify-space-between">
      <span>
        <v-icon class="mr-2">mdi-map-marker-plus</v-icon>
        {{ tl('Change of Registered Address E-Filing') }}
      </span>
      <div class="d-flex align-center ga-2">
        <EfilingWalkthroughButton />
        <v-chip v-if="readOnly" color="success" variant="flat" size="small">
          <v-icon start size="small">mdi-check-circle</v-icon>
          {{ tl('Completed') }}
        </v-chip>
      </div>
    </v-card-title>
    <v-divider />

    <v-card-text>
      <!-- Change Address Form -->
      <ChangeAddressForm
        ref="caFormRef"
        :task-config="taskConfig"
        :task-id="taskId != null ? String(taskId) : undefined"
        :entity-cd="entityCd"
        :loading="eFiling.isSubmitting.value"
        :read-only="readOnly"
        @update:formData="handleFormDataUpdate"
        @entity-pick-request="emit('entity-pick-request')"
        @firm-pick-request="emit('firm-pick-request')"
      />

      <!-- Validation Errors -->
      <v-alert
        v-if="!readOnly && eFiling.validationErrors.value.length > 0"
        type="error"
        variant="tonal"
        class="mt-4"
        closable
        @click:close="eFiling.validationErrors.value = []"
      >
        <div class="font-weight-bold mb-2">{{ tl('Validation Errors') }}:</div>
        <div v-for="(error, index) in eFiling.validationErrors.value" :key="index"
          :class="error.startsWith('  -') ? 'text-body-2 pl-4 text-error' : 'font-weight-medium mt-1'"
          :style="error.startsWith('  -') ? 'opacity: 0.85' : ''"
        >{{ error }}</div>
      </v-alert>

      <!-- Completed task: result -->
      <v-card
        v-if="readOnly && eFiling.resultSuccess.value"
        variant="tonal"
        color="success"
        class="mt-4 pa-4"
      >
        <div class="d-flex align-center mb-3">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          <span class="text-h6 font-weight-bold text-success">
            {{ tl('E-Filing Completed') }}
          </span>
        </div>

        <v-alert
          v-if="eFiling.warningMessage.value"
          type="warning"
          variant="tonal"
          class="mb-3"
          density="compact"
          border="start"
        >
          <div class="d-flex align-start gap-2">
            <v-icon size="16" color="warning">mdi-alert</v-icon>
            <div class="text-body-2">{{ eFiling.warningMessage.value }}</div>
          </div>
        </v-alert>

        <v-btn
          v-if="eFiling.pdfDataBase64.value"
          color="primary"
          variant="flat"
          @click="handleDownloadPdf"
        >
          <v-icon start>mdi-file-pdf-box</v-icon>
          {{ tl('Download Receipt') }}
        </v-btn>
      </v-card>
    </v-card-text>

    <v-divider />

    <v-card-actions class="justify-end pa-4">
      <v-btn
        v-if="showBackButton"
        variant="outlined"
        color="secondary"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="emit('back')"
      >
        <v-icon start>mdi-arrow-left</v-icon>
        {{ tl('Back') }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!readOnly"
        variant="outlined"
        color="primary"
        :loading="eFiling.isSaving.value"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        class="mr-2"
        @click="eFiling.handleTempSave(taskId != null ? String(taskId) : undefined, caFormRef, readOnly)"
      >
        <v-icon start>mdi-content-save-outline</v-icon>
        {{ tl('Temp Save') }}
      </v-btn>
      <v-btn
        v-if="!readOnly"
        color="primary"
        variant="flat"
        :loading="eFiling.isSubmitting.value"
        :disabled="eFiling.isSubmitting.value || eFiling.isSaving.value"
        @click="eFiling.handleSubmit(caFormRef, readOnly, (msg) => emit('error', msg))"
      >
        <v-icon start>mdi-send</v-icon>
        {{ tl('Submit E-Filing') }}
      </v-btn>
    </v-card-actions>

    <!-- Credential Select Dialog -->
    <EFilingCredentialDialog
      v-model="eFiling.showCredentialDialog.value"
      :mycoid-credentials="eFiling.mycoidCredentials.value"
      :selected-id="eFiling.selectedCredentialId.value"
      :is-loading="eFiling.isLoadingCredentials.value"
      :guide-message="tl('CA Credential Required')"
      @select="eFiling.selectCredential"
      @confirm="eFiling.handleCredentialConfirm(readOnly)"
      @manage="eFiling.openManageCredential"
    />

    <!-- Credential Manage Modal -->
    <CredentialManageModal
      v-model="eFiling.showManageCredentialDialog.value"
      @credential-added="eFiling.handleCredentialAdded"
    />

    <!-- Confirm Dialog -->
    <EFilingConfirmDialog
      v-model="eFiling.showConfirmDialog.value"
      :credential="eFiling.selectedCredential.value"
      :message="tl('CA Confirm Message')"
      @confirm="handleConfirmSubmit"
    />

    <!-- Process Dialog -->
    <EFilingProcessDialog
      v-model="eFiling.showProcessDialog.value"
      :step="eFiling.submitStep.value"
      :success="eFiling.resultSuccess.value"
      :message="eFiling.resultMessage.value"
      :pdf-data-base64="eFiling.pdfDataBase64.value"
      :warning-message="eFiling.warningMessage.value"
      :success-title="tl('Address Change Submitted Successfully')"
      :fail-title="tl('Address Change Failed')"
      @close="eFiling.handleProcessDialogClose(router)"
      @download-pdf="handleDownloadPdf"
    >
      <template #result>
        <div class="text-body-1 text-grey-darken-1 mt-2">
          {{ tl('Address change has been submitted successfully.') }}
        </div>
      </template>
    </EFilingProcessDialog>

    <!-- Temp Save Dialog -->
    <EFilingTempSaveDialog
      v-model="eFiling.showTempSaveDialog.value"
      :success="eFiling.tempSaveSuccess.value"
      :message="eFiling.tempSaveMessage.value"
    />
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import ChangeAddressForm from './ChangeAddressForm.vue';
import { changeAddressApi } from '../../api/changeAddressApi';
import { buildPayload } from './composables/useFormPayload';
import { updateEFilingComplete, saveEFilingData } from '@/features/startTask/api/taskApi';
import CredentialManageModal from '@/features/Entity_my/components/credentials/CredentialManageModal.vue';
import { useEFilingSubmit } from '../../composables/useEFilingSubmit';
import { downloadPdf } from '../../composables/usePdfDownload';
import { useDialog } from '@/shared/composables/useDialog';
import EFilingCredentialDialog from '../shared/EFilingCredentialDialog.vue';
import EfilingWalkthroughButton from '../shared/EfilingWalkthroughButton.vue';
import EFilingConfirmDialog from '../shared/EFilingConfirmDialog.vue';
import EFilingProcessDialog from '../shared/EFilingProcessDialog.vue';
import EFilingTempSaveDialog from '../shared/EFilingTempSaveDialog.vue';

const router = useRouter();
const { error: showDownloadError } = useDialog();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Props
interface Props {
  taskData?: any;
  taskId?: string | number;
  entityCd?: string;
  showBackButton?: boolean;
  readOnly?: boolean;
  taskConfig?: any;
  // Standalone 흐름: 좌측 챗봇에서 선택된 entity (StandaloneEFilingPage 가 주입).
  //   값이 바뀌면 ChangeAddressForm.applyEntity 호출 → companyNumber/companyName 자동 채움.
  selectedEntity?: Record<string, any> | null;
  // 챗봇 officer-list 에서 선택된 officer (firmName 매핑).
  selectedOfficer?: Record<string, any> | null;
}

const props = withDefaults(defineProps<Props>(), {
  taskData: null,
  taskId: undefined,
  entityCd: '',
  showBackButton: true,
  readOnly: false,
  taskConfig: null,
  selectedEntity: null,
  selectedOfficer: null,
});

const emit = defineEmits<{
  back: [];
  submit: [data: any];
  error: [message: string];
  // 좌측 챗봇에 entity-selector 위젯을 띄워달라는 부모 신호.
  //   ChangeAddressForm 의 ✨ 아이콘 클릭 → 여기서 부모로 패스스루.
  'entity-pick-request': [];
  // firmName ✨ 아이콘 → 챗봇 officer-list 위젯 요청.
  'firm-pick-request': [];
}>();

// Composable
const eFiling = useEFilingSubmit({ formType: 'M32006' });

// Form ref
const caFormRef = ref<InstanceType<typeof ChangeAddressForm> | null>(null);
const formDataRef = ref<any>(null);
const savedDataLoaded = ref(false);

// Load saved data on mount
onMounted(() => {
  if (props.taskData?.formData) {
    setTimeout(() => {
      if (caFormRef.value?.loadSavedData && !savedDataLoaded.value) {
        savedDataLoaded.value = true;
        caFormRef.value.loadSavedData(props.taskData.formData);
      }
    }, 100);
  }

  if (props.readOnly && props.taskData?.eFilingResult) {
    const result = props.taskData.eFilingResult;
    eFiling.pdfDataBase64.value = result.pdfDataBase64 || null;
    eFiling.warningMessage.value = result.warningMessage || null;
    eFiling.resultSuccess.value = result.success || false;
  }
});

watch(() => props.taskData, (newData) => {
  if (newData?.formData && caFormRef.value?.loadSavedData && !savedDataLoaded.value) {
    savedDataLoaded.value = true;
    caFormRef.value.loadSavedData(newData.formData);
  }
});

// ── ref ready 까지 기다리며 child 메서드 호출하는 헬퍼 ─────────────
async function callOnFormWhenReady<T extends keyof NonNullable<typeof caFormRef.value>>(
  method: T,
  arg: any,
  label: string,
) {
  await nextTick();
  const max = 20;
  for (let i = 0; i < max; i++) {
    const fn = (caFormRef.value as any)?.[method];
    if (typeof fn === 'function') {
      try { fn.call(caFormRef.value, arg); } catch (e) { console.error(`[${label}] threw`, e); }
      return;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  console.warn(`[${label}] caFormRef.${String(method)} not ready after retries`);
}

// 챗봇 officer-list 에서 선택된 officer → firmName 자동 채움.
watch(
  () => props.selectedOfficer,
  (officer) => { if (officer) void callOnFormWhenReady('applyOfficer', officer, 'ChangeAddressEFiling.applyOfficer'); },
  { deep: true, immediate: true },
);

// 챗봇에서 선택된 entity → 폼 자동 채움.
//   immediate: mount 시점에 이미 selectedEntity 가 있는 경우 대비.
//   nextTick: caFormRef 가 child mount 직후 valid 해질 시점 보장.
//   재시도 루프: 동적 component 마운트 / SectionRenderer 초기화 타이밍에 따라
//   첫 호출에 caFormRef 가 null 이거나 applyEntity 가 없을 수 있음.
watch(
  () => props.selectedEntity,
  async (entity) => {
    if (!entity) return;
    await nextTick();
    let attempts = 0;
    const maxAttempts = 20; // ~1초
    while (attempts < maxAttempts) {
      const fn = caFormRef.value?.applyEntity;
      if (typeof fn === 'function') {
        try {
          fn(entity);
          console.log('[ChangeAddressEFiling] applyEntity called', entity);
        } catch (e) {
          console.error('[ChangeAddressEFiling] applyEntity threw', e);
        }
        return;
      }
      attempts++;
      await new Promise((r) => setTimeout(r, 50));
    }
    console.warn('[ChangeAddressEFiling] caFormRef.applyEntity not ready after retries', {
      hasRef: !!caFormRef.value,
      keys: caFormRef.value ? Object.keys(caFormRef.value) : null,
    });
  },
  { deep: true, immediate: true },
);

const handleFormDataUpdate = (data: any) => {
  formDataRef.value = data;
};

const handleDownloadPdf = () => {
  if (!eFiling.pdfDataBase64.value) return;
  try {
    downloadPdf(eFiling.pdfDataBase64.value, 'ChangeAddress_receipt');
  } catch (e: any) {
    console.error('[ChangeAddressEFiling] downloadPdf', e);
    void showDownloadError(
      tl('Error'),
      e?.message || tl('Failed to prepare PDF file. The receipt data may be corrupted.'),
    );
  }
};

// Change Address specific submit logic
const handleConfirmSubmit = async () => {
  if (props.readOnly) {
    eFiling.showConfirmDialog.value = false;
    return;
  }

  const formData = caFormRef.value?.getFormData();
  if (!formData) {
    eFiling.validationErrors.value = ['Failed to get form data'];
    emit('error', 'Failed to get form data');
    return;
  }

  eFiling.startProcessing();

  try {
    // Step 1: SSM Login
    const loginResponse: any = await changeAddressApi.ssmLogin(eFiling.selectedCredentialId.value || '');

    if (!loginResponse.success || !loginResponse.session_id) {
      throw new Error(loginResponse.error || 'SSM Login failed. Please check your MYCOID2016 credential.');
    }

    const sessionId = loginResponse.session_id;

    // Step 2: Build payload and call API
    const payload = buildPayload(formData, sessionId, eFiling.selectedCredentialId.value || undefined);

    console.log('[CA-EFiling] Payload:', {
      unique_entity_number: payload.unique_entity_number,
      firm_name: payload.firm_name,
      state: payload.state,
      city: payload.city,
    });

    const response = await changeAddressApi.changeAddress(payload);

    const apiData = (response as any)?.data || response;

    if (apiData?.success) {
      eFiling.pdfDataBase64.value = apiData.pdf_data_base64 || apiData.pdfDataBase64 || null;
      eFiling.warningMessage.value = apiData.warning || null;

      eFiling.completeSuccess('Address change has been submitted successfully!');

      const completedAtIso = new Date().toISOString();
      const eFilingPayload = {
        taskTypeCd: 'M32102',
        entityCd: props.entityCd || null,
        countryCd: 'MY',
        formData: formData as Record<string, unknown>,
        requestPayload: payload as unknown as Record<string, unknown>,
        responseData: apiData as Record<string, unknown>,
        warningMessage: eFiling.warningMessage.value || null,
        receiptPdfBase64: eFiling.pdfDataBase64.value || null,
        receiptPdfFilename: 'ChangeAddress_receipt.pdf',
        status: 'B36003',
        isSuccess: true,
        completedAt: completedAtIso,
      };

      // taskId 있으면 task 상태 업데이트 (Standalone 은 task 자체가 없으므로 skip).
      const taskId = props.taskId;
      if (taskId) {
        try {
          await updateEFilingComplete(taskId);
        } catch (updateError) {
          console.error('[ChangeAddressEFiling] Task status update failed:', updateError);
        }
      }
      // L_TASK_EFILING_DATA 저장 — taskId null 이면 자동으로 standalone endpoint 사용.
      try {
        await saveEFilingData(taskId ?? null, eFilingPayload);
      } catch (saveError) {
        console.error('[ChangeAddressEFiling] L_TASK_EFILING_DATA save failed:', saveError);
      }

      emit('submit', apiData);
    } else {
      eFiling.completeFromApiResult(apiData, '', 'Change address failed');
    }
  } catch (error: any) {
    const errorMessage = eFiling.completeError(error, 'Failed to change address');
    emit('error', errorMessage);
  }
};
</script>
